/* Merchandiser scorecard — on-time delivery %, delivered/late/overdue split */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Target, TrendingUp, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

const toneRing = {
  green: "ring-emerald-200",
  amber: "ring-amber-200",
  red:   "ring-red-200",
};
const toneText = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red:   "text-red-700",
};

export function MerchScorecard() {
  const [sc, setSc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/merch/scorecard").then(r => setSc(r.data)).catch(() => setErr(true));
  }, []);
  if (err) return null;
  if (!sc) {
    return (
      <div className="hag-surface p-4 text-xs text-[#7C8E9E]" data-testid="merch-scorecard-loading">
        Loading scorecard…
      </div>
    );
  }

  const pct = sc.on_time_pct;
  const pctLabel = pct === null ? "—" : `${pct}%`;
  const barPct = pct === null ? 0 : pct;

  return (
    <div className="hag-surface p-5" data-testid="merch-scorecard">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-16 h-16 rounded-full ring-4 ${toneRing[sc.tone]} flex items-center justify-center bg-white`}>
            <div className={`text-xl font-black leading-none ${toneText[sc.tone]}`}
              data-testid="merch-scorecard-score">
              {sc.score}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E] flex items-center gap-1">
              <Target size={11} /> My Scorecard
            </div>
            <div className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}
              data-testid="merch-scorecard-pct">
              {pctLabel} <span className="text-sm font-medium text-[#4A627A]">on-time delivery</span>
            </div>
            <div className="text-[11px] text-[#7C8E9E]">
              {sc.delivered} of {sc.total_pos} PO{sc.total_pos === 1 ? "" : "s"} delivered
            </div>
          </div>
        </div>

        <Link to="/coordinator/po-register?status=Received" data-testid="merch-scorecard-details"
          className="text-xs px-3 py-1.5 rounded-full border border-[#1B3A5C]/20 hover:bg-[#FAF6F0] font-semibold"
          style={{ color: "var(--brand-navy)" }}>
          Delivery history →
        </Link>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
        <div className={`h-full transition-all ${sc.tone === "green" ? "bg-emerald-500" : sc.tone === "amber" ? "bg-amber-500" : "bg-red-500"}`}
          style={{ width: `${barPct}%` }} data-testid="merch-scorecard-bar" />
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <MetricTile
          testid="merch-tile-ontime"
          icon={<CheckCircle2 size={13} />}
          label="On-time"
          value={sc.on_time}
          tone="emerald"
        />
        <MetricTile
          testid="merch-tile-late"
          icon={<AlertTriangle size={13} />}
          label="Late"
          value={sc.late}
          tone={sc.late > 0 ? "red" : "slate"}
        />
        <MetricTile
          testid="merch-tile-overdue"
          icon={<AlertTriangle size={13} />}
          label="Overdue"
          value={sc.overdue}
          tone={sc.overdue > 0 ? "red" : "slate"}
        />
        <MetricTile
          testid="merch-tile-upcoming"
          icon={<Clock size={13} />}
          label="Due ≤14d"
          value={sc.upcoming_14d}
          tone={sc.upcoming_14d > 0 ? "amber" : "slate"}
        />
        <MetricTile
          testid="merch-tile-later"
          icon={<TrendingUp size={13} />}
          label="Later"
          value={sc.later}
          tone="slate"
        />
      </div>

      {sc.late_examples.length > 0 && (
        <div className="mt-4 p-3 rounded bg-red-50 border border-red-200"
          data-testid="merch-scorecard-late">
          <div className="text-[11px] font-semibold text-red-800 mb-1 flex items-center gap-1">
            <AlertTriangle size={12} /> Recent late deliveries
          </div>
          <ul className="text-xs text-red-900 space-y-0.5">
            {sc.late_examples.map((ex) => (
              <li key={ex.po_number} data-testid={`merch-late-${ex.po_number}`}>
                <span className="font-mono font-semibold">{ex.po_number}</span>
                {" · "}<span>{ex.days_late} day{ex.days_late === 1 ? "" : "s"} late</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[10px] text-[#B0B7BF]">
        Auto-calculated from PO receipt date vs. ETA. Tone: green ≥80 · amber ≥60 · red below.
      </div>
    </div>
  );
}

const toneClass = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800"   },
  red:     { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-800"     },
  slate:   { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-700"   },
};

function MetricTile({ testid, icon, label, value, tone }) {
  const c = toneClass[tone] || toneClass.slate;
  return (
    <div data-testid={testid}
      className={`rounded-md p-2.5 border ${c.bg} ${c.border}`}>
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-widest ${c.text}`}>
        {icon} {label}
      </div>
      <div className={`text-2xl font-black mt-1 ${c.text}`}>{value}</div>
    </div>
  );
}
